import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { batchList } from "../../api/Batch";
import { formatDateTime, SweetAlertShow } from "../master/Master";
import Pagination from "../components/Pagination";
import { studentLoginStatusList } from "../../api/Student";
import { printElement } from "../master/Print";

const TYPES = [
  { value: "", label: "All Students", icon: "ri-team-line" },
  { value: "installed", label: "Installed", icon: "ri-checkbox-circle-line" },
  {
    value: "not_installed",
    label: "Not Installed",
    icon: "ri-smartphone-line",
  },
  { value: "active_3", label: "Active (3 days)", icon: "ri-pulse-line" },
  { value: "inactive_3", label: "Inactive (3 days)", icon: "ri-time-line" },
];
const EMPTY_STATS = {
  total: 0,
  installed: 0,
  not_installed: 0,
  active_3: 0,
  inactive_3: 0,
};
const n = (value) => Number(value || 0).toLocaleString("en-IN");
const errorText = (e) =>
  e?.response?.data?.message ||
  e?.response?.message ||
  e?.message ||
  "Unable to load data.";
const initial = (value) =>
  String(value || "S")
    .trim()
    .charAt(0)
    .toUpperCase();
const isRecent = (value) => {
  if (!value) return false;
  const time = new Date(value).getTime();
  return (
    Number.isFinite(time) &&
    Date.now() - time <= 3 * 86400000 &&
    time <= Date.now()
  );
};

function Stat({ icon, label, value, type, selected, onClick }) {
  return (
    <button
      type="button"
      className={`sals-stat sals-tone-${type} ${selected ? "sals-stat-selected" : ""}`}
      onClick={onClick}
    >
      <span className="sals-stat-icon">
        <i className={icon} />
      </span>
      <span className="sals-stat-content">
        <span className="sals-stat-label">{label}</span>
        <strong>{value}</strong>
      </span>
      <i className="ri-arrow-right-up-line sals-stat-arrow" />
    </button>
  );
}

export default function StudentLoginStatus() {
  const [filters, setFilters] = useState({
    page: 1,
    search: "",
    batch_id: "",
    type: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ data: [] });
  const [stats, setStats] = useState(EMPTY_STATS);
  const [refreshKey, setRefreshKey] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    let current = true;
    setBatchesLoading(true);
    batchList()
      .then((response) => {
        if (!current) return;
        if (response?.data?.status)
          setBatches(
            Array.isArray(response.data.data) ? response.data.data : [],
          );
        else
          SweetAlertShow(
            "info",
            "Notice",
            response?.data?.message || "Could not load batches.",
          );
      })
      .catch((e) => {
        if (current) SweetAlertShow("error", "Error", errorText(e));
      })
      .finally(() => {
        if (current) setBatchesLoading(false);
      });
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    const request = ++requestRef.current;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("page", String(filters.page || 1));
      if (filters.search) params.set("search", filters.search);
      if (filters.batch_id) params.set("batch_id", filters.batch_id);
      if (filters.type) params.set("type", filters.type);
      try {
        const response = await studentLoginStatusList(params.toString());
        if (!mounted || request !== requestRef.current) return;
        if (response?.data?.status) {
          setData(response.data.data || { data: [] });
          setStats(response.data.stats || EMPTY_STATS);
        } else {
          setData({ data: [] });
          SweetAlertShow(
            "info",
            "Notice",
            response?.data?.message || "Could not load students.",
          );
        }
      } catch (e) {
        if (mounted && request === requestRef.current) {
          setData({ data: [] });
          SweetAlertShow("error", "Error", errorText(e));
        }
      } finally {
        if (mounted && request === requestRef.current) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [
    filters.page,
    filters.search,
    filters.batch_id,
    filters.type,
    refreshKey,
  ]);

  const rows = Array.isArray(data?.data) ? data.data : [];
  const installRate = Number(stats.total)
    ? `${Math.round((Number(stats.installed || 0) / Number(stats.total)) * 100)}%`
    : "0%";
  const filtered = Boolean(filters.search || filters.batch_id || filters.type);
  const pageFrom = Number(data?.from ?? 1);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, page: 1, [key]: value }));
  }, []);
  const reset = () => {
    setSearchInput("");
    setFilters({ page: 1, search: "", batch_id: "", type: "" });
  };
  const applySearch = (event) => {
    event?.preventDefault?.();
    setFilter("search", searchInput.trim());
  };

  const statsCards = useMemo(
    () => [
      {
        icon: "ri-team-line",
        label: "Total Students",
        value: n(stats.total),
        type: "primary",
        filter: "",
      },
      {
        icon: "ri-checkbox-circle-line",
        label: "App Installed",
        value: n(stats.installed),
        type: "green",
        filter: "installed",
      },
      {
        icon: "ri-smartphone-line",
        label: "Not Installed",
        value: n(stats.not_installed),
        type: "red",
        filter: "not_installed",
      },
      {
        icon: "ri-pie-chart-line",
        label: "Installation Rate",
        value: installRate,
        type: "purple",
        filter: "installed",
      },
      {
        icon: "ri-pulse-line",
        label: "Active · Last 3 Days",
        value: n(stats.active_3),
        type: "cyan",
        filter: "active_3",
      },
      {
        icon: "ri-time-line",
        label: "Inactive · Last 3 Days",
        value: n(stats.inactive_3),
        type: "amber",
        filter: "inactive_3",
      },
    ],
    [stats, installRate],
  );

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Header />
        <div className="layout-page">
          <Nav />
          <div className="content-wrapper">
            <div className="container-fluid flex-grow-1 container-p-y">
              <div className="row">
                <Master />
                <div className="col-12 sals-page">
                  {/* <div className="sals-panel sals-hero">
                    <div className="sals-hero-main">
                      <span className="sals-heading-icon"><i className="ri-smartphone-line" /></span>
                      <div>
                        <div className="sals-eyebrow">STUDENT MANAGEMENT / MOBILE APPLICATION</div>
                        <h3>Student App Login Status</h3>
                        <p>Track app installation, recent activity and students who need assistance.</p>
                      </div>
                    </div>
                    <div className="sals-hero-actions">
                      <button type="button" className="btn btn-outline-primary" onClick={() => setRefreshKey((v) => v + 1)} disabled={loading}>
                        <i className={`ri-refresh-line me-1 ${loading ? "sals-spin" : ""}`} /> Refresh
                      </button>
                      <button type="button" className="btn btn-primary" onClick={() => printElement("PrintDiv")} disabled={loading || !rows.length}>
                        <i className="ri-printer-line me-1" /> Print Current Page
                      </button>
                    </div>
                  </div> */}

                  <div
                    className="sals-stats"
                    aria-label="App installation and activity summary"
                  >
                    {statsCards.map((item) => (
                      <Stat
                        key={item.label}
                        {...item}
                        selected={
                          filters.type === item.filter && item.filter !== ""
                        }
                        onClick={() => setFilter("type", item.filter)}
                      />
                    ))}
                  </div>

                  <section className="sals-panel sals-list-panel">
                    <div className="sals-panel-top">
                      <div>
                        <h5>
                          Student Activity{" "}
                          <span className="sals-count">
                            {n(data?.total ?? rows.length)}
                          </span>
                        </h5>
                        <p>
                          Filter students by app status or batch to review their
                          latest login activity.
                        </p>
                      </div>
                    </div>

                    <div className="sals-filters">
                      <form
                        onSubmit={applySearch}
                        className="sals-search col-md-3"
                      >
                        <i className="ri-search-line" />
                        <input
                          type="search"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          placeholder="Search name, mobile or registration no."
                          aria-label="Search students"
                        />
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                        >
                          Search
                        </button>
                      </form>
                      <div className="sals-batch-select col-md-2">
                        <i className="ri-graduation-cap-line" />
                        <select
                          aria-label="Select batch"
                          value={filters.batch_id || ""}
                          disabled={batchesLoading}
                          onChange={(e) =>
                            setFilter("batch_id", e.target.value)
                          }
                        >
                          <option value="">
                            {batchesLoading
                              ? "Loading batches..."
                              : "All Batches"}
                          </option>
                          {batches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        className="sals-tabs ms-auto "
                        role="group"
                        aria-label="Filter by app activity"
                      >
                        {TYPES.map((option) => (
                          <button
                            type="button"
                            key={option.value}
                            className={`sals-tab ${filters.type === option.value ? "is-active" : ""}`}
                            aria-pressed={filters.type === option.value}
                            onClick={() => setFilter("type", option.value)}
                          >
                            <i className={option.icon} /> {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {filtered && (
                      <div className="sals-chips">
                        {filters.search && (
                          <button
                            type="button"
                            onClick={() => {
                              setSearchInput("");
                              setFilter("search", "");
                            }}
                          >
                            Search: {filters.search}{" "}
                            <i className="ri-close-line" />
                          </button>
                        )}
                        {filters.batch_id && (
                          <button
                            type="button"
                            onClick={() => setFilter("batch_id", "")}
                          >
                            Batch:{" "}
                            {batches.find(
                              (b) => String(b.id) === String(filters.batch_id),
                            )?.title || filters.batch_id}{" "}
                            <i className="ri-close-line" />
                          </button>
                        )}
                        {filters.type && (
                          <button
                            type="button"
                            onClick={() => setFilter("type", "")}
                          >
                            {TYPES.find((t) => t.value === filters.type)
                              ?.label || filters.type}{" "}
                            <i className="ri-close-line" />
                          </button>
                        )}
                        {filtered && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={reset}
                          >
                            <i className="ri-filter-off-line me-1" /> Clear
                          </button>
                        )}
                      </div>
                    )}

                    <div id="PrintDiv" className="sals-print-area">
                      <div className="sals-print-heading">
                        <h3>Student App Login Status</h3>
                        <p>
                          Showing {rows.length} of{" "}
                          {n(data?.total ?? rows.length)} records · Current
                          page: {filters.page || 1}
                        </p>
                      </div>
                      <div className="table-responsive sals-table-scroll">
                        <table className="table align-middle sals-table mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Student / Registration</th>
                              <th>Contact</th>
                              <th>Batch</th>
                              <th>RTE</th>
                              <th>App Installation & Activity</th>
                              <th>Last Activity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              Array.from({ length: 6 }, (_, i) => (
                                <tr key={`skeleton-${i}`}>
                                  {Array.from({ length: 7 }, (_, j) => (
                                    <td key={j}>
                                      <span className="sals-skeleton" />
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : rows.length ? (
                              rows.map((item, index) => {
                                const info = item?.student_batch_info;
                                const installed = Boolean(item?.fcm_token);
                                const recent =
                                  installed &&
                                  isRecent(item?.fcm_token?.updated_at);
                                const lastSeen = item?.fcm_token?.updated_at;
                                return (
                                  <tr key={item.id || index}>
                                    <td className="sals-index">
                                      {pageFrom + index}
                                    </td>
                                    <td>
                                      <div className="sals-person">
                                        <span className="sals-avatar">
                                          {initial(item?.name)}
                                        </span>
                                        <span className="sals-person-text">
                                          <strong>{item?.name || "—"}</strong>
                                          <small>
                                            Reg. {item?.registration_no || "—"}
                                            {item?.gender
                                              ? ` · ${item.gender}`
                                              : ""}
                                          </small>
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="sals-cell-strong">
                                        {item?.phone || "—"}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="sals-batch-text">
                                        <strong>
                                          {info?.batch?.title || "—"}
                                        </strong>
                                        {info?.course?.title && (
                                          <small>{info.course.title}</small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span
                                        className={`sals-pill ${info?.rte_status === "RTE" ? "sals-pill-blue" : "sals-pill-muted"}`}
                                      >
                                        {info?.rte_status || "—"}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="sals-status-stack">
                                        <span
                                          className={`sals-pill ${installed ? "sals-pill-green" : "sals-pill-red"}`}
                                        >
                                          <i
                                            className={
                                              installed
                                                ? "ri-checkbox-circle-line"
                                                : "ri-close-circle-line"
                                            }
                                          />{" "}
                                          {installed
                                            ? "Installed"
                                            : "Not Installed"}
                                        </span>
                                        {installed && (
                                          <span
                                            className={`sals-substatus ${recent ? "sals-recent" : "sals-idle"}`}
                                          >
                                            <span className="sals-dot" />
                                            {lastSeen
                                              ? recent
                                                ? "Active in last 3 days"
                                                : "Inactive in last 3 days"
                                              : "Activity unavailable"}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="sals-last-seen">
                                        <i className="ri-time-line" />
                                        <span>
                                          {lastSeen
                                            ? formatDateTime(lastSeen)
                                            : "Not available"}
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={7}>
                                  <div className="sals-empty">
                                    <i className="ri-user-search-line" />
                                    <h6>No students found</h6>
                                    <p>
                                      Try another status, batch, or search term.
                                    </p>
                                    {filtered && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={reset}
                                      >
                                        Clear Filters
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {!loading && rows.length > 0 && (
                      <div className="sals-footer">
                        <span>
                          Showing {n(data?.from ?? 1)}–
                          {n(data?.to ?? rows.length)} of{" "}
                          {n(data?.total ?? rows.length)} students
                        </span>
                        <Pagination
                          data={data}
                          setFilters={setFilters}
                          filters={filters}
                        />
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .sals-page{--sals-border:#e8edf5;--sals-muted:#7b879c;--sals-text:#26334b;--sals-bg:#fff;--sals-soft:#f7f9fd;color:var(--sals-text)}
        .sals-panel{background:var(--sals-bg);border:1px solid var(--sals-border);border-radius:16px;box-shadow:0 4px 18px rgba(32,49,85,.035)}
        .sals-hero{display:flex;justify-content:space-between;align-items:center;gap:15px;flex-wrap:wrap;padding:20px 22px;margin-bottom:16px}
        .sals-hero-main{display:flex;align-items:center;gap:14px;min-width:0}.sals-heading-icon{width:48px;height:48px;border-radius:13px;display:flex;align-items:center;justify-content:center;color:#fff;background:linear-gradient(135deg,#6574e9,#5267ce);font-size:23px;flex-shrink:0}
        .sals-eyebrow{font-size:10px;letter-spacing:.8px;color:#8794a9;font-weight:750;margin-bottom:3px}.sals-hero h3{font-size:21px;font-weight:750;margin:0 0 4px}.sals-hero p,.sals-panel-top p{color:var(--sals-muted);font-size:12px;margin:0}.sals-hero-actions{display:flex;gap:8px;flex-wrap:wrap}
        .sals-stats{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:11px;margin-bottom:17px}.sals-stat{min-width:0;min-height:91px;display:flex;align-items:center;gap:10px;text-align:left;position:relative;padding:14px 12px;border-radius:13px;background:var(--sals-bg);border:1px solid var(--sals-border);cursor:pointer;transition:transform .18s,border-color .18s,box-shadow .18s}.sals-stat:hover{transform:translateY(-2px);box-shadow:0 9px 20px rgba(40,54,83,.06)}.sals-stat-selected{border-color:#7582ef;box-shadow:0 0 0 2px rgba(101,116,233,.09)}
        .sals-stat-icon{width:36px;height:36px;border-radius:10px;flex:0 0 36px;display:flex;align-items:center;justify-content:center;font-size:19px;background:#eef1ff;color:#5b69d5}.sals-tone-green .sals-stat-icon{background:#e9f8f0;color:#20975c}.sals-tone-red .sals-stat-icon{background:#fff0f1;color:#d84e64}.sals-tone-purple .sals-stat-icon{background:#f4edff;color:#8e57d1}.sals-tone-cyan .sals-stat-icon{background:#eaf8fd;color:#208daa}.sals-tone-amber .sals-stat-icon{background:#fff6e8;color:#be842c}
        .sals-stat-content{min-width:0;display:flex;flex-direction:column;gap:6px}.sals-stat-label{font-size:11px;color:var(--sals-muted);line-height:1.3}.sals-stat-content strong{font-size:22px;line-height:1;font-weight:750;color:var(--sals-text)}.sals-stat-arrow{margin-left:auto;align-self:flex-start;font-size:12px;color:#bdc6d3}
        .sals-list-panel{overflow:hidden}.sals-panel-top{padding:18px 20px 9px}.sals-panel-top h5{font-size:16px;font-weight:750;margin:0 0 5px}.sals-count{display:inline-flex;background:#ecf0ff;color:#586bd5;border-radius:7px;font-size:11px;padding:3px 8px;vertical-align:middle;margin-left:5px}
        .sals-tabs{display:flex;gap:7px;flex-wrap:wrap;padding:12px 20px}.sals-tab{display:flex;align-items:center;gap:6px;padding:9px 12px;border-radius:9px;border:1px solid #e8edf5;background:#fff;color:#67758e;font-size:12px;font-weight:650;cursor:pointer}.sals-tab i{font-size:15px}.sals-tab:hover{background:#f5f7ff;color:#5666cf}.sals-tab.is-active{background:#586ad8;color:#fff;border-color:#586ad8;box-shadow:0 3px 9px rgba(88,106,216,.17)}
        .sals-filters{display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:0 20px 15px}.sals-search{flex:1 1 330px;display:flex;align-items:center;gap:8px;border:1px solid #e2e7f0;border-radius:9px;padding:5px 6px 5px 12px;min-width:200px}.sals-search>i,.sals-batch-select>i{color:#8795aa;font-size:17px}.sals-search input{width:100%;min-width:0;flex:1;border:0;outline:none;color:inherit;background:transparent;font-size:12px}.sals-batch-select{flex:0 1 270px;min-width:180px;display:flex;gap:8px;align-items:center;border:1px solid #e2e7f0;border-radius:9px;padding:9px 10px}.sals-batch-select select{min-width:0;flex:1;border:0;outline:none;background:transparent;color:inherit;font-size:12px}.sals-chips{display:flex;flex-wrap:wrap;gap:7px;padding:0 20px 14px}.sals-chips button{border:1px solid #dfe5f5;background:#f4f6ff;color:#5b68b3;border-radius:7px;padding:5px 9px;font-size:11px}
        .sals-table-scroll{border-top:1px solid var(--sals-border)}.sals-table{font-size:12px;white-space:nowrap}.sals-table thead th{background:#f6f8fc;color:#758197;font-size:10px;letter-spacing:.45px;text-transform:uppercase;font-weight:750;padding:13px 14px;border-bottom:1px solid var(--sals-border)}.sals-table tbody td{padding:13px 14px;border-bottom:1px solid #f0f2f7;vertical-align:middle}.sals-table tbody tr:hover{background:#fafbff}.sals-index{color:#9aa5b6;font-weight:650}.sals-person{display:flex;align-items:center;gap:10px}.sals-avatar{width:35px;height:35px;flex:0 0 35px;display:flex;align-items:center;justify-content:center;border-radius:10px;background:#ebefff;color:#5266cf;font-weight:800;font-size:15px}.sals-person-text,.sals-batch-text{display:flex;flex-direction:column;gap:4px}.sals-person-text strong,.sals-batch-text strong{color:#33415a;font-size:12px;font-weight:750}.sals-person-text small,.sals-batch-text small{font-size:11px;color:#8995a6}.sals-cell-strong{font-weight:650;color:#4d5b72}
        .sals-pill{display:inline-flex;align-items:center;gap:5px;border-radius:7px;padding:5px 9px;font-size:10px;font-weight:750}.sals-pill-green{background:#e8f8ef;color:#21845b}.sals-pill-red{background:#fff0f0;color:#c95158}.sals-pill-blue{background:#eaf3ff;color:#4279b9}.sals-pill-muted{background:#f0f2f6;color:#748093}.sals-status-stack{display:flex;align-items:flex-start;flex-direction:column;gap:5px}.sals-substatus{font-size:10px;display:inline-flex;align-items:center;gap:5px}.sals-recent{color:#29875c}.sals-idle{color:#ae772e}.sals-dot{width:6px;height:6px;background:currentColor;border-radius:50%}.sals-last-seen{display:flex;align-items:center;gap:6px;color:#6d7b8e;font-size:11px}
        .sals-footer{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;padding:14px 18px;color:#8490a2;font-size:11px}.sals-footer> :last-child{min-width:0}.sals-empty{text-align:center;padding:55px 15px}.sals-empty>i{font-size:43px;color:#a4b2ca}.sals-empty h6{font-size:15px;font-weight:750;margin:9px 0 5px}.sals-empty p{font-size:12px;color:#8c97a9;margin:0 0 12px}.sals-skeleton{display:block;width:85%;height:14px;border-radius:5px;background:linear-gradient(90deg,#edf0f5,#f8f9fc,#edf0f5);background-size:200% 100%;animation:sals-shimmer 1.1s infinite}.sals-print-heading{display:none}
        @keyframes sals-shimmer{to{background-position:-200% 0}}@keyframes sals-spin{to{transform:rotate(360deg)}}.sals-spin{display:inline-block;animation:sals-spin 1s linear infinite}
        @media(max-width:1399px){.sals-stats{grid-template-columns:repeat(3,minmax(0,1fr))}}@media(max-width:767px){.sals-stats{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.sals-hero{padding:15px}.sals-hero h3{font-size:17px}.sals-heading-icon{width:40px;height:40px;font-size:19px}.sals-hero-actions{width:100%}.sals-hero-actions .btn{flex:1}.sals-panel-top{padding:15px 13px 6px}.sals-tabs{flex-wrap:nowrap;overflow-x:auto;padding:10px 13px;scrollbar-width:thin}.sals-tab{flex:0 0 auto}.sals-filters{padding:0 13px 12px}.sals-batch-select{flex:1 1 100%}.sals-search{flex-basis:100%}.sals-stat{padding:11px 9px;min-height:78px}.sals-stat-content strong{font-size:19px}.sals-stat-label{font-size:10px}.sals-stat-icon{width:30px;height:30px;flex-basis:30px;font-size:16px}.sals-stat-arrow{display:none}}
        [data-bs-theme="dark"] .sals-page{--sals-border:#42455d;--sals-muted:#adb4c5;--sals-text:#e8eaf4;--sals-bg:#2b2c40;--sals-soft:#34364b}[data-bs-theme="dark"] .sals-stat,[data-bs-theme="dark"] .sals-tab{background:var(--sals-bg)}[data-bs-theme="dark"] .sals-table thead th{background:#34364b}[data-bs-theme="dark"] .sals-table tbody tr:hover{background:#34364b}[data-bs-theme="dark"] .sals-person-text strong,[data-bs-theme="dark"] .sals-batch-text strong{color:#e7e9f5}
        @media print{.sals-page .sals-hero,.sals-page .sals-stats,.sals-page .sals-panel-top,.sals-page .sals-tabs,.sals-page .sals-filters,.sals-page .sals-chips,.sals-page .sals-footer{display:none!important}.sals-print-heading{display:block;margin-bottom:15px}.sals-print-heading h3{font-size:18px}.sals-print-heading p{font-size:11px}.sals-table{white-space:normal;font-size:10px}.sals-table-scroll{overflow:visible!important}.sals-table tbody td,.sals-table thead th{padding:7px!important}thead{display:table-header-group}tr{page-break-inside:avoid}}
      `}</style>
    </div>
  );
}
